import I18n from 'I18n';
import PortalIdParser from 'PortalIdParser';
import { getPropertyValue } from 'framework-asset-preview-lib/internal/shared/utils/crmObject';
import { STATUS } from 'framework-asset-preview-lib/internal/shared/constants/assetPreview';
import allSettled from 'framework-asset-preview-lib/internal/shared/utils/allSettled';
import { fetchEmailOpenDurationAggregated, fetchEmailPerformance, fetchEmailRevenue } from './api';
import TimeSpentViewingBars from './components/TimeSpentViewingBars';
import { AUTOMATED_LIKE, DEFAULT_REVENUE_RESPONSE, EMAIL_TYPE } from './constants';
import { jsx as _jsx } from "react/jsx-runtime";
export function getNonCrmPropertyValue(propertyName, nonCrmObject, crmObject) {
  if (!nonCrmObject) {
    return undefined;
  }
  const marketingEmailResponse = nonCrmObject;
  const maybeVariantEmail = crmObject && isABVariant(crmObject) ? marketingEmailResponse.variant : marketingEmailResponse.primaryEmail;
  switch (propertyName) {
    default:
      return undefined;
    case 'id':
      return marketingEmailResponse.primaryEmail.id;
    case 'fromName':
      return marketingEmailResponse.primaryEmail.fromName;
    case 'subject':
      return marketingEmailResponse.primaryEmail.subject;
    case 'replyTo':
      return marketingEmailResponse.primaryEmail.replyTo;
    case 'subscriptionName':
      return marketingEmailResponse.primaryEmail.subscriptionName;
    case 'updated':
      return marketingEmailResponse.primaryEmail.updated;
    case 'isGraymailSuppressionEnabled':
      return marketingEmailResponse.primaryEmail.isGraymailSuppressionEnabled;
    case 'authorName':
      return marketingEmailResponse.primaryEmail.authorName;
    case 'author':
      return marketingEmailResponse.primaryEmail.author;
    case 'mailingIlsListsIncluded':
      return marketingEmailResponse.primaryEmail.mailingIlsListsIncluded;
    case 'mailingIlsListsExcluded':
      return marketingEmailResponse.primaryEmail.mailingIlsListsExcluded;
    case 'state':
      return marketingEmailResponse.primaryEmail.state;
    case 'publishedByEmail':
      return marketingEmailResponse.primaryEmail.publishedByEmail;
    case 'publishedByName':
      return marketingEmailResponse.primaryEmail.publishedByName;
    case 'rssUrl':
      return marketingEmailResponse.primaryEmail.rssUrl;
    case 'subscriptionBlogId':
      return marketingEmailResponse.primaryEmail.subscriptionBlogId;
    case 'allCampaignIds':
      return marketingEmailResponse.allCampaignIds;
    case 'emailCampaignSends':
      return marketingEmailResponse.emailCampaignSends;
    case 'lastUpdateTime':
      return marketingEmailResponse.rssToEmail ? marketingEmailResponse.rssToEmail.lastUpdateTime : undefined;
    case 'vidsIncluded':
      return marketingEmailResponse.primaryEmail.vidsIncluded;
    case 'vidsExcluded':
      return marketingEmailResponse.primaryEmail.vidsExcluded;
    case 'webVersionUrl':
      return maybeVariantEmail.pageExpiryEnabled && maybeVariantEmail.pageRedirected ? maybeVariantEmail.pageExpiryRedirectUrl : maybeVariantEmail.absoluteUrl;
    case 'webVersionRedirectUrl':
      return maybeVariantEmail.pageExpiryRedirectUrl;
    case 'webVersionRedirectDate':
      return maybeVariantEmail.pageExpiryDate;
  }
}

/**
 * From https://git.hubteam.com/HubSpot/ContentEmailUI/blob/4c2255352c0018c0c1f853a4d4f643d0c4bd6462/static/js/selectors/details/current/currentPrimaryEmailCampaignId.js
 *
 * Get @hs_primary_email_campaign_id to fetch for statistics
 */
export function getPrimaryEmailCampaignId(crmObject, marketingEmailObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  if (isDraftEmail(crmObject)) {
    return null;
  }
  if (emailType === EMAIL_TYPE.AUTOMATED_EMAIL) {
    return marketingEmailObject.emailCampaign ? marketingEmailObject.emailCampaign.apiEmailCampaignId.toString() : null;
  } else if (emailType === EMAIL_TYPE.AB_EMAIL) {
    return marketingEmailObject.abEmailCampaign ? marketingEmailObject.abEmailCampaign.campaignGroupId.toString() : null;
  } else if (emailType === EMAIL_TYPE.RSS_EMAIL || emailType === EMAIL_TYPE.BLOG_EMAIL) {
    return null;
  } else if (emailType === EMAIL_TYPE.LOCALTIME_EMAIL || emailType === EMAIL_TYPE.BATCH_EMAIL) {
    return marketingEmailObject.primaryEmail.primaryEmailCampaignId.toString();
  }
  return null;
}

// Copied from ContentUtils/Routes
export function getParentBlogUrl(subscriptionBlogId) {
  const baseUrl = `/website/${PortalIdParser.get()}/blog/posts`;
  return subscriptionBlogId ? `${baseUrl}?blog=${subscriptionBlogId}` : baseUrl;
}
export function isAbEmail(crmObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  return [EMAIL_TYPE.AB_EMAIL].includes(`${emailType}`);
}
export function isBatchEmail(crmObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  return `${emailType}` === EMAIL_TYPE.BATCH_EMAIL;
}
export function isLocalTimeEmail(crmObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  return `${emailType}` === EMAIL_TYPE.LOCALTIME_EMAIL;
}
export function isABLike(crmObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  return [EMAIL_TYPE.AUTOMATED_AB_EMAIL, EMAIL_TYPE.AB_EMAIL].includes(`${emailType}`);
}
export function isABVariant(crmObject) {
  return ['ab_variant', 'ab_loser_variant', 'automated_ab_variant'].includes(`${getPropertyValue(crmObject, 'hs_subcategory')}`);
}
export function isDraftEmail(crmObject) {
  const publishStatus = getPropertyValue(crmObject, 'hs_publish_status');
  return `${publishStatus}` === STATUS.DRAFT;
}
export function isAutomatedEmail(crmObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  return `${emailType}` === EMAIL_TYPE.AUTOMATED_EMAIL;
}
export function isAbAutomatedEmail(crmObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  return `${emailType}` === EMAIL_TYPE.AUTOMATED_AB_EMAIL;
}
export function isAutomatedLike(crmObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  return AUTOMATED_LIKE.includes(`${emailType}`);
}
export function isFollowUpEmail(crmObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  return `${emailType}` === EMAIL_TYPE.FOLLOWUP_EMAIL;
}
export function isSingleSendApi(crmObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  return `${emailType}` === EMAIL_TYPE.SINGLE_SEND_API;
}
export function getEmailState(nonCrmObject) {
  return getNonCrmPropertyValue('state', nonCrmObject);
}
export function getSubCategory(nonCrmObject) {
  return nonCrmObject ? nonCrmObject.primaryEmail.subcategory : '';
}
export function isScheduledEmail(crmObject) {
  const publishStatus = getPropertyValue(crmObject, 'hs_publish_status');
  return `${publishStatus}` === STATUS.SCHEDULED;
}
export function isPublishedEmail(crmObject) {
  const publishStatus = getPropertyValue(crmObject, 'hs_publish_status');
  return `${publishStatus}` === STATUS.PUBLISHED;
}
export function isAutomatedForForm(crmObject, nonCrmObject) {
  return isFollowUpEmail(crmObject) || getEmailState(nonCrmObject) === 'AUTOMATED_FOR_FORM' || getSubCategory(nonCrmObject) === 'automated_for_form';
}
export function isAutomatedForDeal(crmObject, nonCrmObject) {
  return isAutomatedEmail(crmObject) && getSubCategory(nonCrmObject) === 'automated_for_deal';
}
export function isBlogType(crmObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  const isBlogEmail = EMAIL_TYPE.BLOG_EMAIL === emailType;
  const isBlogEmailChild = EMAIL_TYPE.BLOG_EMAIL_CHILD === emailType;
  return isBlogEmail || isBlogEmailChild;
}
export function isRssType(crmObject) {
  const emailType = getPropertyValue(crmObject, 'hs_type');
  const isRssEmail = EMAIL_TYPE.RSS_EMAIL === emailType;
  const isRssEmailChild = EMAIL_TYPE.RSS_EMAIL_CHILD === emailType;
  return isRssEmail || isRssEmailChild;
}
export function isBlogOrRssType(crmObject) {
  return isBlogType(crmObject) || isRssType(crmObject);
}
export function getFrequencyTimeDetails({
  time,
  repeats,
  repeats_on_weekly,
  repeats_on_monthly
}) {
  return {
    time: I18n.moment(time, 'h:mm a').format('LT'),
    day: repeats === 'weekly' ? I18n.moment(repeats_on_weekly, 'd').format('dddd') : '',
    monthlyDate: repeats === 'monthly' ? I18n.moment(repeats_on_monthly, 'D').format('Do') : ''
  };
}
export function isAvailableToWorkflows(crmObject) {
  return (isAutomatedEmail(crmObject) || isAbAutomatedEmail(crmObject)) && !isAutomatedForForm(crmObject) && !isAutomatedForDeal(crmObject) && !isSingleSendApi(crmObject);
}
export function getCanLoadAutomationByEmail(crmObject) {
  const automationForRegularEmailsCheck = isBatchEmail(crmObject) || isLocalTimeEmail(crmObject) || isAbEmail(crmObject);
  return isAvailableToWorkflows(crmObject) || automationForRegularEmailsCheck;
}
export function getCounterValue({
  emailStatistics,
  statName
}) {
  if (!emailStatistics || !emailStatistics.aggregate || !(statName in emailStatistics.aggregate.counters)) {
    return 0;
  }
  return emailStatistics.aggregate.counters[statName];
}
export function getRatio(numerator, denominator) {
  if (!numerator || !denominator) {
    return 0;
  }
  return numerator / denominator * 100;
}

/**
 * Calculate email ratios.
 * Copied from https://git.hubteam.com/HubSpot/EmailUILib/blob/7860065bfdb4a6e6eed8e52b1678bc33aebeb8e3/EmailData/static/js/models/statistics/StatisticsRatios.js#L49
 */
export function getRatioValue({
  emailStatistics,
  statName
}) {
  if (!emailStatistics || !emailStatistics.aggregate) {
    return 0;
  }
  const {
    counters
  } = emailStatistics.aggregate;
  switch (statName) {
    default:
      return 0;
    case 'bounceratio':
      return getRatio(counters.bounce, counters.sent);
    case 'clickratio':
      return getRatio(counters.click, counters.delivered);
    case 'clickthroughratio':
      return getRatio(counters.click, counters.open);
    case 'contactslostratio':
      return getRatio(counters.contactslost, counters.sent);
    case 'deliveredratio':
      return getRatio(counters.delivered, counters.sent);
    case 'hardbounceratio':
      return getRatio(counters.hardbounced, counters.sent);
    case 'notsentratio':
      return 100 - getRatio(counters.sent, counters.selected);
    case 'openratio':
      return getRatio(counters.open, counters.delivered);
    case 'pendingratio':
      return getRatio(counters.pending, counters.sent);
    case 'replyratio':
      return getRatio(counters.reply, counters.delivered);
    case 'softbounceratio':
      return getRatio(counters.softbounced, counters.sent);
    case 'spamreportratio':
      return getRatio(counters.spamreport, counters.delivered);
    case 'unsubscribedratio':
      return getRatio(counters.unsubscribed, counters.delivered);
  }
}
export function getEmailPerformanceStats({
  crmObject,
  nonCrmObject,
  options
}) {
  const emailId = getPropertyValue(crmObject, 'hs_origin_asset_id');
  const emailPerformancePromise = !emailId ? Promise.resolve(undefined) : fetchEmailPerformance(emailId);
  const emailRevenuePromise = options.hasScope('revenue-attribution-read') ? fetchEmailRevenue(nonCrmObject) : Promise.resolve(DEFAULT_REVENUE_RESPONSE);
  const isPublished = isPublishedEmail(crmObject);
  const fetchEmailOpenDurationAggregatedPromise = isPublished ? fetchEmailOpenDurationAggregated : () => Promise.resolve({
    glanced: 0,
    read: 0,
    skimmed: 0
  });
  return allSettled([emailPerformancePromise, fetchEmailOpenDurationAggregatedPromise(nonCrmObject), emailRevenuePromise]).then(promises => {
    const [emailStatisticsResponse, openDurationAggregatedResponse, emailRevenueResponse] = promises;
    const emailStatistics = emailStatisticsResponse.status === 'fulfilled' ? emailStatisticsResponse.value : undefined;
    const openDurationAggregated = openDurationAggregatedResponse.status === 'fulfilled' ? openDurationAggregatedResponse.value : {
      glanced: 0,
      read: 0,
      skimmed: 0
    };
    const emailRevenueStats = emailRevenueResponse.status === 'fulfilled' ? emailRevenueResponse.value : undefined;
    const isAbLikeEmail = isABLike(crmObject);
    const overviewPerfGroup = {
      stats: [{
        formatStyle: 'percentage',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-29.hs_open_rate'),
        propertyName: 'hs_open_rate',
        value: getRatioValue({
          emailStatistics,
          statName: 'openratio'
        })
      }, {
        formatStyle: 'percentage',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-29.hs_click_through_rate'),
        propertyName: 'hs_click_through_rate',
        value: getRatioValue({
          emailStatistics,
          statName: 'clickthroughratio'
        })
      }, {
        formatStyle: 'percentage',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-29.hs_click_rate'),
        propertyName: 'hs_click_rate',
        value: getRatioValue({
          emailStatistics,
          statName: 'clickratio'
        })
      }, {
        formatStyle: 'percentage',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-29.hs_unsubscribes'),
        propertyName: 'hs_unsubscribes',
        value: getRatioValue({
          emailStatistics,
          statName: 'unsubscribedratio'
        })
      }],
      title: I18n.text('frameworkAssetPreviewLib.tabs.performance.performanceGroup.0-29.overview')
    };
    const openPerformancePerfGroup = {
      stats: [{
        formatStyle: 'decimal',
        propertyName: 'hs_delivered',
        value: getCounterValue({
          emailStatistics,
          statName: 'delivered'
        })
      }, {
        formatStyle: 'percentage',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-29.hs_open_rate'),
        propertyName: 'hs_open_rate',
        value: getRatioValue({
          emailStatistics,
          statName: 'openratio'
        })
      }, !isAbLikeEmail && {
        formatStyle: 'text',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-29.hs_subject'),
        propertyName: 'subject',
        value: nonCrmObject ? nonCrmObject.primaryEmail.subject : undefined
      }, !isAbLikeEmail && {
        formatStyle: 'text',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-29.hs_from_name'),
        propertyName: 'fromName',
        value: nonCrmObject ? nonCrmObject.primaryEmail.fromName : undefined
      }, !isAbLikeEmail && {
        formatStyle: 'text',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-29.hs_from_address'),
        propertyName: 'replyTo',
        value: nonCrmObject ? nonCrmObject.primaryEmail.replyTo : undefined
      }].filter(Boolean),
      title: I18n.text('frameworkAssetPreviewLib.tabs.performance.performanceGroup.0-29.openPerformance')
    };
    const clickPerformancePerfGroup = {
      stats: [{
        formatStyle: 'percentage',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-29.hs_click_rate'),
        propertyName: 'hs_click_rate',
        value: getRatioValue({
          emailStatistics,
          statName: 'clickratio'
        })
      }, {
        formatStyle: 'percentage',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-29.hs_click_through_rate'),
        propertyName: 'hs_click_through_rate',
        value: getRatioValue({
          emailStatistics,
          statName: 'clickthroughratio'
        })
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_number_of_links_clicked',
        value: getCounterValue({
          emailStatistics,
          statName: 'click'
        })
      }],
      title: I18n.text('frameworkAssetPreviewLib.tabs.performance.performanceGroup.0-29.clickPerformance')
    };
    const emailHealthPerfGroup = {
      stats: [{
        formatStyle: 'decimal',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-29.hs_unsubscribes'),
        propertyName: 'hs_unsubscribes',
        value: getCounterValue({
          emailStatistics,
          statName: 'unsubscribed'
        })
      }],
      title: I18n.text('frameworkAssetPreviewLib.tabs.performance.performanceGroup.0-29.emailHealth')
    };
    const emailRevenuePerfGroup = {
      stats: [{
        formatStyle: 'currency',
        propertyName: 'hs_revenue_attributed_revenue',
        value: emailRevenueStats ? emailRevenueStats.metrics.hs_linear_amount.sum : 0
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_revenue_deals',
        value: emailRevenueStats ? emailRevenueStats.metrics.hs_deal_id.distinctApprox : 0
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_revenue_contacts',
        value: emailRevenueStats ? emailRevenueStats.metrics.hs_contact_id.distinctApprox : 0
      }],
      title: I18n.text('frameworkAssetPreviewLib.tabs.performance.performanceGroup.0-29.revenue')
    };
    const deliverabilityPerfGroup = {
      stats: [{
        formatStyle: 'decimal',
        propertyName: 'hs_successful_deliveries',
        value: getCounterValue({
          emailStatistics,
          statName: 'delivered'
        })
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_not_sent',
        value: getCounterValue({
          emailStatistics,
          statName: 'notsent'
        })
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_spam_reports',
        value: getCounterValue({
          emailStatistics,
          statName: 'spamreport'
        })
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_hard_bounces',
        value: getCounterValue({
          emailStatistics,
          statName: 'hardbounced'
        })
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_soft_bounces',
        value: getCounterValue({
          emailStatistics,
          statName: 'softbounced'
        })
      }],
      title: I18n.text('frameworkAssetPreviewLib.tabs.performance.performanceGroup.0-29.deliverability')
    };
    const readabilityPerfGroup = {
      stats: [{
        formatStyle: 'elementWithoutLabel',
        propertyName: 'hs_test',
        value: /*#__PURE__*/_jsx(TimeSpentViewingBars, {
          openDurationAggregated: openDurationAggregated
        })
      }],
      title: I18n.text('frameworkAssetPreviewLib.tabs.performance.performanceGroup.0-29.readability')
    };
    return [overviewPerfGroup, openPerformancePerfGroup, clickPerformancePerfGroup, emailHealthPerfGroup, emailRevenuePerfGroup, deliverabilityPerfGroup, readabilityPerfGroup];
  });
}